import _get from 'lodash/get';
import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { BACKGROUND_CONTENT_OFFSET, defaultContext, getInitialStyle } from './hero.style';
import { templates, sizes, imageOptions } from './hero.constants';
import HeroOverlay from './heroOverlay/heroOverlay.component';
import HeroMetadata from './heroMetadata/heroMetadata.component';
import HeroNav from './heroNav/heroNav.component';
import { sizes as heroNavSizes } from './heroNav/heroNav.style';
import { FOCUSED_SIGNAL, PORTION_REQUESTED_SIGNAL, SELECTED_SIGNAL } from '../signals';
import { Row } from '../row/row.component';

/**
 * A large format that allows several programmes to be displayed on a full page thanks to the automatic scrolling of a carousel.
 * It is often found on the home page of the platform, at the top of the screen.
 *
 * @version 1.8.2
 * @see https://zeroheight.com/726d5c282/p/3550b0-hero--v182
 */
export default class Hero extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {},
        Gradient: {
          type: HeroOverlay,
        },
        Metadata: {
          type: HeroMetadata,
        },
        Items: {
          type: Row,
          signals: {
            [FOCUSED_SIGNAL]: '_setIndex',
          },
        },
        passSignals: {
          [SELECTED_SIGNAL]: true,
          [PORTION_REQUESTED_SIGNAL]: true,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
    this._contentPadding = 0;
  }

  _detach() {
    this._items = undefined;
    this._style = undefined;
    this._index = undefined;
    this._contentPadding = 0;
  }

  get id() {
    return this._id;
  }

  get data() {
    return this._data;
  }

  set data(value) {
    const { block } = value;
    this._id = block.id;
    this._items = block.content.items;

    // NOTE: in order to follow Bedrock Design System language, Hero is an item and SlideShow is a block, so placing actual item here
    this.tag('Items').data = {
      ...value,
      itemType: HeroNav,
      itemTemplate: 'HeroPoster',
      itemSize: heroNavSizes[this._style.screenSize],
    };
    this._setIndex(this.focusedIndex);
  }

  get contentPadding() {
    return this._contentPadding;
  }

  set contentPadding(value) {
    if (value === this._contentPadding) {
      return;
    }

    this._contentPadding = Number.isFinite(value) ? value : 0;

    const { screenSize } = this._style;
    const x = this._contentPadding;
    const backgroundOffset = BACKGROUND_CONTENT_OFFSET[screenSize];
    const backgroundSize = imageOptions[templates.Hero][RENDERING_CONTEXT.GENERAL][screenSize].PRIMARY;
    const w = backgroundSize.width - x + backgroundOffset;
    const h = Math.round(backgroundSize.height * (w / backgroundSize.width));

    this.patch({
      Background: { w, h },
      Gradient: { w, h },
      Metadata: { x },
      Items: {
        x,
        leftPadding: value,
      },
    });
  }

  set background(value) {
    this.tag('Background').src = value;
  }

  get focusMode() {
    return this.tag('Items').focusMode;
  }

  set focusMode(value) {
    this.tag('Items').focusMode = value;
  }

  get focusedIndex() {
    return this.tag('Items').focusedIndex;
  }

  set focusedIndex(value) {
    this.tag('Items').focusedIndex = value;
    this._setIndex(value);
  }

  _getFocused() {
    return this.tag('Items');
  }

  _setIndex(index) {
    if (!this._items || !this._items.length || index === this._index) {
      return;
    }

    this._index = Number.isFinite(index) ? index : 0;

    const currentItem = this._items[this._index];
    if (currentItem) {
      this.tag('Metadata').item = currentItem;
    }

    this.background = _get(this._items, `[${this._index}].image.src`, '');
  }
}

Hero.Templates = templates;
Hero.Sizes = sizes;
Hero.ImageOptions = imageOptions;
